import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import {
  PARTICIPANT_TABLE_PAGE_SIZE,
  PARTICIPANT_TABLE_SEARCH_THRESHOLD,
  useParticipantTable,
  type UseParticipantTableOptions,
  type UseParticipantTableResult,
} from '@src/components/ParticipantTable/useParticipantTable';

const PAGINATION_FOOTER_RESERVE_PX = 52;
const SEARCH_TOOLBAR_FALLBACK_PX = 48;
const RANGE_FALLBACK_PX = 24;
const CARD_TOP_CHROME_FALLBACK_PX = 56;
const MIN_ROWS = 1;
const MAX_ROWS = 50;
const FALLBACK_ROW_HEIGHT_PX = 72;

export interface UseParticipantTableMaxRowsOptions {
  fallback?: number;
  /** Minimum page size on phones when the table can grow beyond the viewport. */
  mobileMinRows?: number;
  desktopMinRows?: number;
  /** When false the table is not mounted — skip until it appears. */
  enabled?: boolean;
  /** Reserve toolbar height when search is expected but not painted yet. */
  showSearch?: boolean;
  /** Reserve range + footer height when pagination is expected. */
  willPaginate?: boolean;
}

export interface ParticipantTableMaxRowsResult {
  maxRows: number;
  rowHeight: number;
}

export interface UseParticipantTableLayoutOptions {
  /** When false the table is not mounted — skip until it appears. */
  enabled?: boolean;
  fillViewport: boolean;
  /** Active page size — set only while paginating. */
  pageSize?: number;
  rowHeight: number;
}

function fillContainer(root: HTMLElement): HTMLElement | null {
  return root.closest('[data-participant-table-fill]');
}

function isDesktopPageFill(fill: HTMLElement): boolean {
  return (
    fill.classList.contains('participant-table-fill--desktop-page') &&
    window.matchMedia('(min-width: 48.0625rem)').matches
  );
}

function isPageFill(fill: HTMLElement): boolean {
  return fill.classList.contains('participant-table-fill--page') || isDesktopPageFill(fill);
}

/** Browser chrome and keyboards must not change the management page capacity. */
function stableManagementViewportHeight(fill: HTMLElement): number | null {
  if (!fill.closest('.game-participants') || !window.matchMedia('(max-width: 48rem)').matches) {
    return null;
  }
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;height:100svh';
  document.body.appendChild(probe);
  const height = probe.getBoundingClientRect().height;
  probe.remove();
  return height || window.innerHeight;
}

/** Full viewport below the sticky header — for scroll-to-table dashboard layout. */
function measurePageFillHeight(fill: HTMLElement): number {
  const viewportHeight = stableManagementViewportHeight(fill) ?? window.visualViewport?.height ?? window.innerHeight;
  // The quick-draw intro scrolls away, so its table gets a whole desktop viewport.
  if (isDesktopPageFill(fill)) return viewportHeight;
  const mobileContent = fill.closest('.mobile-box-layout__content');
  if (mobileContent) {
    // The bottom navigation hides while scrolling. Keep a stable page size
    // using the full viewport, with only the content's edge padding reserved.
    const style = getComputedStyle(mobileContent);
    return Math.max(
      0,
      viewportHeight - (parseFloat(style.paddingTop) || 0) - (parseFloat(style.paddingBottom) || 0),
    );
  }
  const header = document.querySelector('[data-testid="app-header"]') as HTMLElement | null;
  const headerHeight = header?.offsetHeight ?? 0;
  let reserve = 0;
  const pageColumn = fill.closest('.container--pad-box, .container--pad-page');
  if (pageColumn) {
    reserve += parseFloat(getComputedStyle(pageColumn).paddingBottom || '0') || 0;
  }
  return Math.max(0, viewportHeight - headerHeight - reserve);
}

function readPeekPx(): number {
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:absolute;visibility:hidden;height:var(--space-8);pointer-events:none';
  document.body.appendChild(probe);
  const height = probe.getBoundingClientRect().height;
  probe.remove();
  return height || 32;
}

function readBelowFillReserve(fill: HTMLElement): number {
  let reserve = readPeekPx();
  let sibling = fill.nextElementSibling;
  while (sibling) {
    reserve += sibling.getBoundingClientRect().height;
    sibling = sibling.nextElementSibling;
  }
  const parent = fill.parentElement;
  if (parent && fill.nextElementSibling) {
    reserve += parseFloat(getComputedStyle(parent).rowGap || '0') || 0;
  }
  const pageColumn = fill.closest('.container--pad-box, .container--pad-page');
  if (pageColumn) {
    reserve += parseFloat(getComputedStyle(pageColumn).paddingBottom || '0') || 0;
  }
  return reserve;
}

function usesFlexFill(fill: HTMLElement): boolean {
  return Boolean(fill.closest('.randomizer-exclusions__main'));
}

function readCssPx(name: string, fallback: number): number {
  const probe = document.createElement('div');
  probe.style.cssText = `position:absolute;visibility:hidden;pointer-events:none;height:var(${name});`;
  document.body.appendChild(probe);
  const height = probe.getBoundingClientRect().height;
  probe.remove();
  return height || fallback;
}

function readToolbarHeight(table: HTMLElement): number {
  const toolbar = table.querySelector('.participant-table__toolbar');
  const measured = toolbar?.getBoundingClientRect().height ?? 0;
  if (measured > 0) {
    return measured;
  }
  const sample = document.querySelector('.participant-table__toolbar');
  return sample?.getBoundingClientRect().height || SEARCH_TOOLBAR_FALLBACK_PX;
}

function readRangeHeight(table: HTMLElement): number {
  const rangeRow = table.querySelector('.participant-table__range-row');
  const measured = rangeRow?.getBoundingClientRect().height ?? 0;
  if (measured > 0) {
    return measured;
  }
  const range = table.querySelector('.participant-table__range');
  if (range && range.getBoundingClientRect().height > 0) {
    return range.getBoundingClientRect().height;
  }
  return RANGE_FALLBACK_PX;
}

function readCardSlotGap(): number {
  return readCssPx('--space-3', 12);
}

function readCardTopChrome(table: HTMLElement, fill: HTMLElement): number {
  const fillTop = fill.getBoundingClientRect().top;
  const card = table.querySelector('.participant-table.panel');
  if (card) {
    const paddingTop = parseFloat(getComputedStyle(card).paddingTop) || 0;
    return Math.max(0, card.getBoundingClientRect().top - fillTop + paddingTop);
  }
  return CARD_TOP_CHROME_FALLBACK_PX;
}

function measureChromeAboveList(
  table: HTMLElement,
  fill: HTMLElement,
  options: Pick<UseParticipantTableMaxRowsOptions, 'showSearch' | 'willPaginate'>,
): number {
  const fillTop = fill.getBoundingClientRect().top;
  const list = table.querySelector('.participant-table__list');
  const fromList = list ? Math.max(0, list.getBoundingClientRect().top - fillTop) : 0;

  let fromParts = readCardTopChrome(table, fill);
  const panel = table.querySelector('.participant-table');
  const slotGap = panel ? parseFloat(getComputedStyle(panel).rowGap) || 0 : readCardSlotGap();
  const toolbar = table.querySelector('.participant-table__toolbar');
  if (toolbar) {
    fromParts += toolbar.getBoundingClientRect().height + slotGap;
  } else if (options.showSearch) {
    fromParts += readToolbarHeight(table) + slotGap;
  }

  const rangeRow = table.querySelector('.participant-table__range-row');
  if (rangeRow) {
    fromParts += rangeRow.getBoundingClientRect().height + slotGap;
  } else if (
    options.willPaginate &&
    !toolbar?.classList.contains('participant-table__toolbar--compact')
  ) {
    fromParts += readRangeHeight(table) + slotGap;
  }

  return Math.max(fromList, fromParts);
}

/** Space below the rows, including panel padding and gaps around pagination. */
function measureFooter(table: HTMLElement, willPaginate: boolean): number {
  const panel = table.querySelector<HTMLElement>('.participant-table');
  const list = table.querySelector<HTMLElement>('.participant-table__list');
  const footer = table.querySelector('.participant-table__footer');
  const measured = footer?.getBoundingClientRect().height ?? 0;
  let reserve = measured > 0 ? measured : willPaginate ? PAGINATION_FOOTER_RESERVE_PX : 0;
  if (panel && list) {
    const panelStyle = getComputedStyle(panel);
    const listStyle = getComputedStyle(list);
    const gap = parseFloat(panelStyle.rowGap) || 0;
    reserve +=
      (parseFloat(panelStyle.paddingBottom) || 0) +
      (parseFloat(panelStyle.borderBottomWidth) || 0) +
      (parseFloat(listStyle.paddingTop) || 0) +
      (parseFloat(listStyle.paddingBottom) || 0);
    if (measured > 0 || willPaginate) reserve += gap;
    for (let sibling = list.nextElementSibling; sibling; sibling = sibling.nextElementSibling) {
      if (sibling !== footer) reserve += sibling.getBoundingClientRect().height + gap;
    }
  }
  return reserve;
}

function measureFillHeight(fill: HTMLElement): number {
  if (isPageFill(fill)) {
    return measurePageFillHeight(fill);
  }
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  const fillTop = fill.getBoundingClientRect().top;
  return Math.max(0, viewportHeight - fillTop - readBelowFillReserve(fill));
}

function measureListSpace(
  table: HTMLElement,
  fill: HTMLElement,
  options: Pick<UseParticipantTableMaxRowsOptions, 'showSearch' | 'willPaginate'>,
): number {
  const tableFooter = measureFooter(table, Boolean(options.willPaginate));

  if (isPageFill(fill)) {
    const fillHeight = measurePageFillHeight(fill);
    const chrome = measureChromeAboveList(table, fill, options);
    return Math.max(0, fillHeight - chrome - tableFooter);
  }

  const list = table.querySelector('.participant-table__list');
  const viewportBottom = (window.visualViewport?.height ?? window.innerHeight) - readPeekPx();
  const pageFooter = readBelowFillReserve(fill) - readPeekPx();

  if (list) {
    const listTop = list.getBoundingClientRect().top;
    const fromViewport = viewportBottom - listTop - tableFooter - pageFooter;
    if (fromViewport > 0) {
      return fromViewport;
    }
  }

  const fillHeight = measureFillHeight(fill);
  const chrome = measureChromeAboveList(table, fill, options);
  return Math.max(0, fillHeight - chrome - tableFooter);
}

function measureRowHeight(root: HTMLElement): number {
  const rows = root.querySelectorAll(
    '.participant-table__list .row--large:not(.participant-table__row-placeholder)',
  );
  let height = 0;
  for (const row of rows) {
    // Measure the content, not the fixed height applied for pagination.
    // Otherwise an older, taller row size can never shrink after a resize.
    const style = getComputedStyle(row);
    const contentHeight = Math.max(
      0,
      ...Array.from(row.children, (child) => child.getBoundingClientRect().height),
    );
    const next =
      contentHeight +
      (parseFloat(style.paddingTop) || 0) +
      (parseFloat(style.paddingBottom) || 0) +
      (parseFloat(style.borderTopWidth) || 0) +
      (parseFloat(style.borderBottomWidth) || 0);
    if (next > 0) {
      height = Math.max(height, next);
    }
  }
  return height || FALLBACK_ROW_HEIGHT_PX;
}

/**
 * How many rows fit when the table fills one viewport below the app header.
 * Independent of the current fill container height.
 */
export function useParticipantTableMaxRows(
  tableRef: RefObject<HTMLElement | null>,
  options: UseParticipantTableMaxRowsOptions = {},
): ParticipantTableMaxRowsResult {
  const {
    fallback = PARTICIPANT_TABLE_PAGE_SIZE,
    mobileMinRows = MIN_ROWS,
    desktopMinRows = MIN_ROWS,
    enabled = true,
    showSearch = false,
    willPaginate = false,
  } = options;
  const [maxRows, setMaxRows] = useState(fallback);
  const footerReserveRef = useRef({ width: 0, height: 0 });
  const [rowHeight, setRowHeight] = useState(FALLBACK_ROW_HEIGHT_PX);

  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    const root = tableRef.current;
    if (!root) {
      return;
    }

    const layoutOptions = { showSearch, willPaginate };
    const observedRows = new WeakSet<Element>();
    // Measure representative rows before paging. Later pages may contain shorter
    // names/statuses, but must not redefine the capacity of this screen.
    let mobileBudget: { width: number; rowHeight: number; reserve: number } | null = null;

    const observer = new ResizeObserver(measure);

    function observeRows() {
      const table = tableRef.current;
      if (!table) {
        return;
      }
      for (const row of table.querySelectorAll(
        '.participant-table__list .row--large:not(.participant-table__row-placeholder)',
      )) {
        if (!observedRows.has(row)) {
          observedRows.add(row);
          observer.observe(row);
        }
      }
    }

    function measure() {
      const table = tableRef.current;
      if (!table) {
        return;
      }

      const fill = fillContainer(table);
      if (!fill) {
        return;
      }

      const header = document.querySelector('[data-testid="app-header"]') as HTMLElement | null;
      const headerHeight = header?.offsetHeight ?? 0;
      document.documentElement.style.setProperty('--size-app-header-measured', `${headerHeight}px`);

      const measuredRowHeight = measureRowHeight(table);
      setRowHeight((current) => (current === measuredRowHeight ? current : measuredRowHeight));

      let listSpace = measureListSpace(table, fill, layoutOptions);
      // Keep the largest footer at this width: page-count changes can wrap the
      // controls, otherwise row count oscillates between two pagination layouts.
      const width = table.getBoundingClientRect().width;
      const footer = measureFooter(table, Boolean(layoutOptions.willPaginate));
      if (footerReserveRef.current.width !== width)
        footerReserveRef.current = { width, height: footer };
      footerReserveRef.current.height = Math.max(footerReserveRef.current.height, footer);
      listSpace -= footerReserveRef.current.height - footer;
      const stableHeight = stableManagementViewportHeight(fill);
      let capacityRowHeight = measuredRowHeight;
      if (stableHeight !== null) {
        if (!mobileBudget || mobileBudget.width !== width) {
          mobileBudget = { width, rowHeight: measuredRowHeight, reserve: stableHeight - listSpace };
        }
        capacityRowHeight = mobileBudget.rowHeight;
        listSpace = Math.max(0, stableHeight - mobileBudget.reserve);
      } else {
        mobileBudget = null;
      }
      const fit = Math.max(
        window.matchMedia('(max-width: 48rem)').matches ? mobileMinRows : desktopMinRows,
        Math.min(MAX_ROWS, Math.floor(listSpace / capacityRowHeight)),
      );

      setMaxRows((current) => (current === fit ? current : fit));
      observeRows();
    }

    observer.observe(root);
    const fill = fillContainer(root);
    if (fill) {
      observer.observe(fill);
      if (fill.parentElement) {
        observer.observe(fill.parentElement);
      }
      let sibling = fill.nextElementSibling;
      while (sibling) {
        observer.observe(sibling);
        sibling = sibling.nextElementSibling;
      }
      const hero = fill.closest('.randomizer-exclusions')?.querySelector('.randomizer-step-hero');
      if (hero) {
        observer.observe(hero);
      }
    }

    for (const selector of [
      '.participant-table__toolbar',
      '.participant-table__range-row',
      '.participant-table__range',
      '.participant-table__list',
      '.participant-table__footer',
    ]) {
      const node = root.querySelector(selector);
      if (node) {
        observer.observe(node);
      }
    }

    // Pagination/search can mount new list and footer nodes after the initial effect.
    let contentFrame = 0;
    const contentObserver = new MutationObserver(() => {
      cancelAnimationFrame(contentFrame);
      contentFrame = requestAnimationFrame(measure);
    });
    contentObserver.observe(root, { childList: true, subtree: true });
    measure();
    const raf = requestAnimationFrame(measure);

    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    const header = document.querySelector('[data-testid="app-header"]');
    if (header) {
      observer.observe(header);
    }

    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(contentFrame);
      contentObserver.disconnect();
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, [tableRef, enabled, showSearch, willPaginate, mobileMinRows, desktopMinRows]);

  return { maxRows, rowHeight };
}

/** Viewport fill + row metrics for a stable paginated layout. */
export function useParticipantTableLayout(
  tableRef: RefObject<HTMLElement | null>,
  { enabled = true, fillViewport, pageSize, rowHeight }: UseParticipantTableLayoutOptions,
): void {
  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    const root = tableRef.current;
    if (!root) {
      return;
    }

    const fill = fillContainer(root);
    if (!fill) {
      return;
    }

    function apply() {
      const table = tableRef.current;
      if (!table) {
        return;
      }

      const host = fillContainer(table);
      if (!host) {
        return;
      }

      const flexFill = usesFlexFill(host);
      host.classList.toggle('participant-table-fill--viewport', fillViewport);
      host.classList.toggle('participant-table-fill--flex', fillViewport && flexFill);

      const minimumHeight =
        isDesktopPageFill(host) && pageSize
          ? measureChromeAboveList(table, host, {}) +
            rowHeight * pageSize +
            measureFooter(table, true)
          : 0;
      const cappedFillHeight = `${Math.max(measureFillHeight(host), minimumHeight)}px`;

      if (fillViewport && pageSize) {
        host.style.setProperty('--participant-table-row-height', `${rowHeight}px`);
        host.style.setProperty('--participant-table-page-size', String(pageSize));
        host.style.setProperty('--participant-table-fill-max-height', cappedFillHeight);
        if (flexFill) {
          host.style.removeProperty('--participant-table-fill-height');
        } else {
          host.style.setProperty('--participant-table-fill-height', cappedFillHeight);
        }
      } else {
        host.style.removeProperty('--participant-table-row-height');
        host.style.removeProperty('--participant-table-page-size');
        host.style.removeProperty('--participant-table-fill-height');
        host.style.removeProperty('--participant-table-fill-max-height');
      }
    }

    apply();

    window.addEventListener('resize', apply);
    window.visualViewport?.addEventListener('resize', apply);

    const observer = new ResizeObserver(apply);
    observer.observe(fill);
    let sibling = fill.nextElementSibling;
    while (sibling) {
      observer.observe(sibling);
      sibling = sibling.nextElementSibling;
    }

    return () => {
      window.removeEventListener('resize', apply);
      window.visualViewport?.removeEventListener('resize', apply);
      observer.disconnect();
      fill.classList.remove('participant-table-fill--viewport');
      fill.classList.remove('participant-table-fill--flex');
      fill.style.removeProperty('--participant-table-row-height');
      fill.style.removeProperty('--participant-table-page-size');
      fill.style.removeProperty('--participant-table-fill-height');
      fill.style.removeProperty('--participant-table-fill-max-height');
    };
  }, [tableRef, enabled, fillViewport, pageSize, rowHeight]);
}

export type UseParticipantTableViewportOptions<T> = Omit<
  UseParticipantTableOptions<T>,
  'maxPageSize' | 'pageSize'
> & {
  enabled?: boolean;
  pageSize?: number;
  showSearch?: boolean;
  willPaginate?: boolean;
  mobileMinRows?: number;
  desktopMinRows?: number;
};

export interface UseParticipantTableViewportResult<T> extends UseParticipantTableResult<T> {
  tableRef: RefObject<HTMLDivElement | null>;
  maxRows: number;
  rowHeight: number;
}

/**
 * Search, pagination, viewport row count and fill layout in one place.
 * The table stays content-sized until the filtered list exceeds one viewport page.
 */
export function useParticipantTableViewport<T>({
  enabled = true,
  pageSize,
  showSearch,
  willPaginate,
  mobileMinRows,
  desktopMinRows,
  items,
  alwaysSearch = false,
  alwaysUseMaxPageSize = false,
  ...rest
}: UseParticipantTableViewportOptions<T>): UseParticipantTableViewportResult<T> {
  const tableRef = useRef<HTMLDivElement>(null);
  const resolvedShowSearch =
    showSearch ?? (alwaysSearch || items.length > PARTICIPANT_TABLE_SEARCH_THRESHOLD);
  const resolvedWillPaginate = willPaginate ?? items.length > PARTICIPANT_TABLE_PAGE_SIZE;

  const { maxRows, rowHeight } = useParticipantTableMaxRows(tableRef, {
    enabled,
    showSearch: resolvedShowSearch,
    willPaginate: resolvedWillPaginate,
    mobileMinRows,
    desktopMinRows,
  });

  const table = useParticipantTable({
    items,
    alwaysSearch,
    alwaysUseMaxPageSize,
    maxPageSize: pageSize ?? maxRows,
    ...rest,
  });

  const paginating = table.pageSize !== undefined;
  const fillViewport = paginating && pageSize === undefined;

  useParticipantTableLayout(tableRef, {
    enabled,
    fillViewport,
    pageSize: table.pageSize,
    rowHeight,
  });

  return {
    tableRef,
    maxRows,
    rowHeight,
    ...table,
  };
}

/** @deprecated Use useParticipantTableLayout. */
export function useParticipantTableViewportFill(
  tableRef: RefObject<HTMLElement | null>,
  enabled: boolean,
): void {
  useParticipantTableLayout(tableRef, {
    fillViewport: enabled,
    rowHeight: FALLBACK_ROW_HEIGHT_PX,
  });
}

/** @deprecated Use useParticipantTableMaxRows. */
export function useFitParticipantPageSize(
  tableRef: RefObject<HTMLElement | null>,
  options: { itemCount?: number; fallback?: number } = {},
): number {
  return useParticipantTableMaxRows(tableRef, options).maxRows;
}
